import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { resetPassword, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [step] = useState(token ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      toast.success(t('auth.resetSuccess'));
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }

    if (password.length < 8) {
      toast.error(t('auth.password') + ' (≥ 8)');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success(t('auth.passwordReset'));
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (success && step === 'request') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('auth.resetSuccess')}</h1>
          <Button onClick={() => navigate('/auth')} data-testid="reset-back-login-button" className="w-full bg-amber-600 hover:bg-amber-700">
            {t('auth.loginBtn')}
          </Button>
        </div>
      </div>
    );
  }

  if (success && step === 'reset') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('auth.passwordReset')}</h1>
          <Button onClick={() => navigate('/auth')} data-testid="reset-go-login-button" className="w-full bg-amber-600 hover:bg-amber-700">
            {t('auth.loginBtn')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <Link to="/auth" className="text-slate-600 hover:text-slate-900 flex items-center mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('auth.loginBtn')}
        </Link>

        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-amber-600" />
        </div>

        {step === 'request' ? (
          <>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">{t('auth.resetTitle')}</h1>
            <p className="text-slate-600 text-center mb-6">{t('auth.resetSubtitle')}</p>

            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email')}
                  required
                  data-testid="reset-email-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={loading}
                data-testid="request-reset-btn"
              >
                {loading ? t('common.loading') : t('auth.sendResetLink')}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">{t('auth.resetTitle')}</h1>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="password">{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.newPassword')}
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility-button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.confirmPassword')}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={loading}
                data-testid="reset-password-btn"
              >
                {loading ? t('common.loading') : t('auth.resetTitle')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
